import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import '../styles/SessionDetails.css';

function SessionDetails({ 
  session, 
  participants, 
  onUpdatePayment, 
  onDeleteParticipant, 
  onBlockSession, 
  onUnblockSession,
  isAdmin
}) {
  const navigate = useNavigate();
  const { sessionId } = useParams();
  const [blockReason, setBlockReason] = useState('');
  const [isBlocking, setIsBlocking] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedParticipant, setSelectedParticipant] = useState(null);

  // Gérer le cas où aucune session n'est sélectionnée
  if (!session) {
    return (
      <div className="no-session-selected">
        <p>Aucune session sélectionnée.</p>
        <button onClick={() => navigate('/')} className="calendar-button">
          Retour au calendrier
        </button>
      </div>
    );
  }

  // Fonction pour formater la date
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', { 
      weekday: 'long', 
      day: 'numeric', 
      month: 'long', 
      year: 'numeric' 
    });
  };

  // Calculer les statistiques de paiement (seulement pour l'admin)
  const totalPlayers = participants.reduce((total, p) => total + (p.playerCount || 1), 0);
  const paidCount = isAdmin ? participants.reduce((total, p) => p.paid ? total + (p.playerCount || 1) : total, 0) : 0;
  const unpaidCount = totalPlayers - paidCount;

  // Fonction pour mettre à jour le statut de paiement
  const togglePaymentStatus = async (participant) => {
    const success = await onUpdatePayment(participant.id, !participant.paid);
    if (!success) {
      alert("Erreur lors de la mise à jour du statut de paiement.");
    }
  };

  // Fonction pour supprimer un participant
  const deleteParticipant = async () => {
    if (!selectedParticipant) return;

    const success = await onDeleteParticipant(selectedParticipant.id);
    if (success) {
      setIsDeleting(false);
      setSelectedParticipant(null);
    } else {
      alert("Erreur lors de la suppression du participant.");
    }
  };

  // Fonction pour bloquer une session
  const blockSession = async () => {
    if (!blockReason) {
      alert("Veuillez indiquer une raison pour le blocage.");
      return;
    }

    const success = await onBlockSession(session.id, blockReason);
    if (success) {
      setIsBlocking(false);
      setBlockReason('');
    } else {
      alert("Erreur lors du blocage de la session.");
    }
  };

  // Fonction pour débloquer une session
  const unblockSession = async () => {
    const success = await onUnblockSession(session.id);
    if (!success) {
      alert("Erreur lors du déblocage de la session.");
    }
  };

  // Fonction pour exporter les participants au format CSV
  const exportParticipantsCSV = () => {
    if (!participants.length) return;
    const header = ['Nom', 'Téléphone', 'Email', 'Joueurs', 'Payé'];
    const rows = participants.map(p =>
      [
        `"${p.name || ''}"`,
        `"${p.phone || ''}"`,
        `"${p.email || ''}"`,
        p.playerCount || 1,
        p.paid ? 'Oui' : 'Non'
      ].join(',')
    );
    const csvContent = [header.join(','), ...rows].join('\r\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `participants-session-${session.id}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="session-details-container">
      <div className="session-details-header">
        <button onClick={() => navigate(`/daily/${session.date}`)} className="back-button">
          &lt; Retour
        </button>
        <h2>
          Session du {formatDate(session.date)} à {session.time}
        </h2>
      </div>
      
      <div className={session.isBlocked ? "blocked-session" : ""}>
        {session.isBlocked && (
          <div className="blocked-notification">
            <h3>Session bloquée</h3>
            <p>{session.blockReason}</p>
            {isAdmin && (
              <button 
                onClick={unblockSession}
                className="unblock-button"
              >
                Débloquer cette session
              </button>
            )}
          </div>
        )}

        <div className="participants-section">
          <div className="participants-header">
            <h3>Participants ({participants.length})</h3>
            <button 
              onClick={() => navigate(`/add-participant/${session.id}`)}
              className="add-participant-button"
            >
              + Ajouter un participant
            </button>
            {isAdmin && (
              <button
                onClick={exportParticipantsCSV}
                className="export-participants-button"
                style={{ marginLeft: 10 }}
              >
                Exporter CSV
              </button>
            )}
          </div>
          
          {participants.length > 0 ? (
            isAdmin ? (
              // Tableau complet pour les administrateurs
              <table className="participants-table">
                <thead>
                  <tr>
                    <th>Nom</th>
                    <th>Téléphone</th>
                    <th>Email</th>
                    <th>Âges des joueurs</th>
                    <th>Joueurs</th>
                    <th>Statut</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {participants.map(participant => (
                    <tr key={participant.id}>
                      <td>{participant.name}</td>
                      <td>{participant.phone}</td>
                      <td className="participant-email">{participant.email || 'Non renseigné'}</td>
                      <td>{participant.ages || 'Non renseigné'}</td>
                      <td>{participant.playerCount || 1}</td>
                      <td>
                        <button
                          onClick={() => togglePaymentStatus(participant)}
                          className={`payment-status-button ${participant.paid ? 'paid' : 'unpaid'}`}
                        >
                          {participant.paid ? 'Payé' : 'Non payé'}
                        </button>
                      </td>
                      <td>
                        <button 
                          onClick={() => {
                            setSelectedParticipant(participant);
                            setIsDeleting(true);
                          }}
                          className="delete-button"
                        >
                          Supprimer
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              // Affichage simplifié pour les non-administrateurs (sans noms)
              <div className="participants-simple-list">
                {participants.map((participant, index) => (
                  <div key={participant.id} className="participant-simple-item">
                    <span className="participant-number">Réservation #{index + 1}</span>
                    <span className="participant-ages">
                      {participant.ages ? `Âges: ${participant.ages}` : 'Âges non renseignés'}
                    </span>
                    <span className="participant-players">{participant.playerCount || 1} joueur(s)</span>
                  </div>
                ))}
              </div>
            )
          ) : (
            <div className="no-participants">
              <p>Aucun participant inscrit à cette session.</p>
            </div>
          )}
          
          {participants.length > 0 && (
            <div className="payment-summary">
              <div className="summary-item">
                <span className="count">{totalPlayers}</span>
                <span className="label">joueur(s) total</span>
              </div>
              {isAdmin && (
                <>
                  <div className="summary-item paid">
                    <span className="count">{paidCount}</span>
                    <span className="label">payé(s)</span>
                  </div>
                  <div className="summary-item unpaid">
                    <span className="count">{unpaidCount}</span>
                    <span className="label">non payé(s)</span>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Modal de confirmation de suppression - seulement pour admin */}
      {isAdmin && isDeleting && selectedParticipant && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Supprimer le participant</h3>
            <p>
              Êtes-vous sûr de vouloir supprimer {selectedParticipant.name} ?
            </p>
            <div className="modal-actions">
              <button 
                onClick={() => {
                  setIsDeleting(false);
                  setSelectedParticipant(null);
                }}
                className="cancel-button"
              >
                Annuler
              </button>
              <button 
                onClick={deleteParticipant}
                className="confirm-button"
              >
                Confirmer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de blocage de session - seulement pour admin */}
      {isAdmin && isBlocking && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Bloquer cette session</h3>
            <p>
              Les participants existants seront supprimés.
            </p>
            <div className="form-group">
              <label htmlFor="blockReason">Raison du blocage:</label>
              <input 
                type="text"
                id="blockReason"
                value={blockReason}
                onChange={(e) => setBlockReason(e.target.value)}
                placeholder="Ex: Réservé pour l'école Saint-Michel"
                className="block-reason-input"
              />
            </div>
            <div className="modal-actions">
              <button 
                onClick={() => {
                  setIsBlocking(false);
                  setBlockReason('');
                }}
                className="cancel-button"
              >
                Annuler
              </button>
              <button 
                onClick={blockSession}
                className="confirm-button"
              >
                Bloquer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SessionDetails;